import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  Input,
  PLATFORM_ID,
} from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import Swal from "sweetalert2";
import { AuthenticationService } from "../../../core/services/authentication.service";

/**
 * Shared auto-logout popup component.
 *
 * Behaviour:
 *   1. After {@link idleTimeoutMs} of no user activity, a confirmation popup
 *      opens (instead of logging out immediately).
 *   2. Cancelling resets the idle timer and keeps the session. Cancel is any of:
 *      the Cancel button, a mouse click outside the popup (backdrop), or Escape.
 *   3. Confirm Logout (the confirm button only) triggers logout immediately.
 *   4. The popup shows a {@link confirmTimeoutMs} countdown; if the user does
 *      not respond in time, logout is triggered automatically.
 *
 * "Activity" deliberately EXCLUDES bare mouse movement: a jittering/idle mouse
 * must not keep a session alive (see {@link ACTIVITY_EVENTS}). Qualifying
 * activity is clicks, keyboard/text input, dropdown + file-input changes,
 * scroll/wheel, and touch.
 *
 * Idle state is synchronised across browser tabs of the same origin via
 * `localStorage` + the `window` `storage` event: qualifying activity in one tab
 * resets every other tab's idle countdown, and a logout in one tab tears down
 * the idle watch in the others. Cross-tab coordination degrades gracefully to
 * per-tab behaviour when storage is unavailable.
 *
 * Rendered once near the app root: `<app-auto-logout></app-auto-logout>`. It has
 * no visible template of its own — the popup is driven by SweetAlert2 so the
 * dialog matches the rest of the app's confirmation dialogs.
 */
@Component({
  selector: "app-auto-logout",
  template: "",
  standalone: true,
})
export class AutoLogoutComponent implements OnInit, OnDestroy {
  /** Inactivity window before the confirmation popup opens (default 15 min). */
  @Input() idleTimeoutMs = 15 * 60 * 1000;

  /** How long the popup waits for a response before auto-logout (default 3 min). */
  @Input() confirmTimeoutMs = 3 * 60 * 1000;

  /**
   * Throttle window for activity events: a burst of events (e.g. scroll)
   * resets the idle timer at most once per this interval to avoid thrashing.
   * Also bounds how often this tab writes the cross-tab activity signal.
   */
  private readonly ACTIVITY_THROTTLE_MS = 3 * 1000;

  /**
   * Browser-only activity events that count as "user is active".
   *
   * Deliberately excludes `mousemove` (and hover events `mouseover`/
   * `mouseenter`): mouse movement alone is treated as idle so an unattended but
   * cursor-jittering workstation still times out. `input` catches text entry
   * including paste, IME composition, autofill and drag-dropped text; `change`
   * catches `<select>` dropdown selection and `<input type="file">` upload
   * selection via bubbling; `touchstart` catches tablet taps.
   */
  private readonly ACTIVITY_EVENTS = [
    "mousedown",
    "click",
    "keydown",
    "input",
    "change",
    "scroll",
    "touchstart",
    "wheel",
  ];

  /** localStorage key: timestamp of the most recent qualifying activity (any tab). */
  private readonly LS_ACTIVITY_KEY = "auto-logout:lastActivity";

  /** localStorage key: logout signal broadcast to other tabs. */
  private readonly LS_LOGOUT_KEY = "auto-logout:loggedOut";

  private readonly isBrowser: boolean;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private confirmCountdownTimer: ReturnType<typeof setInterval> | null = null;
  private lastActivity = 0;
  private activityHandler: (() => void) | null = null;
  private storageHandler: ((event: StorageEvent) => void) | null = null;

  /** True while the confirmation popup is open; suppresses idle-timer resets. */
  private confirmOpen = false;

  constructor(
    private authService: AuthenticationService,
    private toastr: ToastrService,
    private router: Router,
    @Inject(PLATFORM_ID) platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    // SSR-safety: nothing below touches browser globals on the server.
    if (!this.isBrowser) {
      return;
    }

    // Only run the idle timer for a logged-in session.
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) {
      return;
    }

    this.activityHandler = () => this.onActivity();
    for (const eventName of this.ACTIVITY_EVENTS) {
      document.addEventListener(eventName, this.activityHandler, {
        passive: true,
      });
    }

    // Cross-tab coordination: react to activity/logout signals from other tabs.
    this.storageHandler = (event: StorageEvent) => this.onStorage(event);
    window.addEventListener("storage", this.storageHandler);

    this.startIdleTimer();
  }

  ngOnDestroy(): void {
    if (!this.isBrowser) {
      return;
    }
    this.teardownIdleWatch();
  }

  /** (Re)start the idle countdown from a clean window. */
  private startIdleTimer(): void {
    this.clearIdleTimer();
    this.idleTimer = setTimeout(() => this.onIdleTimeout(), this.idleTimeoutMs);
  }

  private clearIdleTimer(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  /**
   * Activity handler for THIS tab. Throttled so a flood of events (scroll)
   * only resets the countdown occasionally. While the popup is open we ignore
   * background activity here — cancelling is handled by the popup itself
   * (Cancel button / outside-click / Escape) so the Confirm Logout button is
   * the only thing that triggers logout. Qualifying local activity is also
   * broadcast to other tabs.
   */
  private onActivity(): void {
    if (this.confirmOpen) {
      return;
    }
    const now = Date.now();
    if (now - this.lastActivity < this.ACTIVITY_THROTTLE_MS) {
      return;
    }
    this.lastActivity = now;
    this.startIdleTimer();
    // Broadcast to other tabs (throttled to the same window as above).
    this.broadcastActivity(now);
  }

  /**
   * Reset the idle countdown in response to activity observed in ANOTHER tab.
   * Does NOT re-broadcast (guards against a storage-event echo loop) and does
   * not disturb an open confirmation popup in this tab.
   */
  private onRemoteActivity(): void {
    if (this.confirmOpen) {
      return;
    }
    // Keep this tab's local throttle baseline aligned without re-broadcasting.
    this.lastActivity = Date.now();
    this.startIdleTimer();
  }

  /** Handle `storage` events from other tabs (activity or logout signals). */
  private onStorage(event: StorageEvent): void {
    if (event.key === this.LS_ACTIVITY_KEY && event.newValue) {
      this.onRemoteActivity();
      return;
    }
    if (event.key === this.LS_LOGOUT_KEY && event.newValue) {
      // Another tab logged out: stop watching and leave the authenticated area.
      this.teardownIdleWatch();
      this.router.navigate(["/auth/login2"]);
    }
  }

  /** Write the cross-tab activity timestamp. Never throws (graceful fallback). */
  private broadcastActivity(timestamp: number): void {
    try {
      localStorage.setItem(this.LS_ACTIVITY_KEY, String(timestamp));
    } catch {
      // Storage unavailable (private mode / quota): fall back to per-tab only.
    }
  }

  /** Signal logout to other tabs. Never throws (graceful fallback). */
  private broadcastLogout(): void {
    try {
      localStorage.setItem(this.LS_LOGOUT_KEY, String(Date.now()));
    } catch {
      // Storage unavailable: other tabs keep their own idle watch.
    }
  }

  /**
   * The user was idle for the whole window: open the confirmation popup with a
   * countdown instead of logging out straight away.
   */
  private onIdleTimeout(): void {
    if (this.confirmOpen) {
      return;
    }
    this.confirmOpen = true;

    const deadline = Date.now() + this.confirmTimeoutMs;

    // Live countdown shown inside the popup, refreshed once per second.
    this.confirmCountdownTimer = setInterval(() => {
      const el = Swal.getHtmlContainer()?.querySelector(
        "#auto-logout-countdown",
      );
      if (el) {
        el.textContent = this.formatRemaining(deadline - Date.now());
      }
    }, 1000);

    Swal.fire({
      title: "Session Timeout",
      html:
        "You have been inactive. You will be logged out automatically in " +
        `<b id="auto-logout-countdown">${this.formatRemaining(
          this.confirmTimeoutMs,
        )}</b>.` +
        "<br/><br/>Click Cancel to stay logged in.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm Logout",
      cancelButtonText: "Cancel",
      // No-response deadline drives the auto-logout.
      timer: this.confirmTimeoutMs,
      timerProgressBar: true,
      // Mouse click outside the popup (backdrop) and Escape both = Cancel.
      allowOutsideClick: true,
      allowEscapeKey: true,
      reverseButtons: true,
    }).then((result) => {
      this.stopConfirmCountdown();
      this.confirmOpen = false;

      if (result.isConfirmed) {
        // (3) Confirm Logout button -> trigger logout.
        this.performLogout();
        return;
      }

      if (result.dismiss === Swal.DismissReason.timer) {
        // (4) No response within the confirm window -> trigger logout.
        this.performLogout();
        return;
      }

      // (2) Cancel button / outside-click / Escape -> reset the idle timer.
      this.lastActivity = Date.now();
      this.startIdleTimer();
    });
  }

  /** Perform the actual logout + redirect, preserving the existing error UX. */
  private performLogout(): void {
    // Tell the other tabs first, then stop watching before we navigate away.
    this.broadcastLogout();
    this.teardownIdleWatch();

    this.authService
      .userLogout()
      .then(() => {
        this.router.navigate(["/auth/login2"]);
      })
      .catch((error) => {
        console.error("Logout failed:", error);
        this.router.navigate(["/auth/login2"]).then(() => {
          this.toastr.error(error ?? "Logout failed");
        });
      });
  }

  /** Format a millisecond remainder as M:SS (never negative). */
  private formatRemaining(ms: number): string {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }

  private stopConfirmCountdown(): void {
    if (this.confirmCountdownTimer !== null) {
      clearInterval(this.confirmCountdownTimer);
      this.confirmCountdownTimer = null;
    }
  }

  /** Clear timers, close any open popup, and remove all listeners. */
  private teardownIdleWatch(): void {
    this.clearIdleTimer();
    this.stopConfirmCountdown();
    if (this.confirmOpen) {
      this.confirmOpen = false;
      Swal.close();
    }
    if (this.activityHandler) {
      for (const eventName of this.ACTIVITY_EVENTS) {
        document.removeEventListener(eventName, this.activityHandler);
      }
      this.activityHandler = null;
    }
    if (this.storageHandler) {
      window.removeEventListener("storage", this.storageHandler);
      this.storageHandler = null;
    }
  }
}
